import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useConfigStore } from '@renderer/stores/configState'
import { normalizeCurrency } from '@renderer/utils/formatCurrency'

/**
 * Language UX is project-scoped: when the loaded project is in IDR
 * (Indonesian operator audience), the customer-facing screens offer the
 * EN/ID toggle and persist whichever the user picked. For any other
 * currency the kiosk is talking to a non-Indonesian audience — the EN/ID
 * toggle is irrelevant noise and we force English regardless of what the
 * operator left in `localStorage`.
 *
 * Two pieces:
 *   - `useShouldShowLanguageSelector()` — read from any component that
 *     renders a language picker (the shared `LanguageSelector` plus the
 *     bahela / bahela-quick inline toggles). Returns true when there's
 *     no project loaded (login, register, dashboard) OR when the loaded
 *     project's currency is IDR / unstamped.
 *   - `useEnforceProjectLanguage()` — mount once in `ProjectRoute`. On
 *     load and whenever the project's `resolvedCurrency` changes, force
 *     `i18n.language` to `en` for non-IDR projects. Never writes to
 *     `localStorage` — the operator's persisted global preference stays
 *     intact so leaving the project (or opening an IDR one next) goes
 *     back to whatever they picked.
 */

/**
 * "IDR-context" means the language picker is meaningful: the project is
 * stamped IDR, or no project is loaded at all (operator chrome / login /
 * dashboard / fresh kiosk install pre-config). Legacy projects without a
 * `resolvedCurrency` are treated as IDR — that's the kiosk's historical
 * default and what `normalizeCurrency` falls back to anyway.
 */
function isIdrContext(currency: string | null | undefined): boolean {
  if (!currency) return true // No project / pre-config — treat as IDR
  return normalizeCurrency(currency) === 'IDR'
}

export function useShouldShowLanguageSelector(): boolean {
  const config = useConfigStore((s) => s.config)
  return isIdrContext(config?.resolvedCurrency)
}

export function useEnforceProjectLanguage(): void {
  const { i18n } = useTranslation()
  const resolvedCurrency = useConfigStore((s) => s.config?.resolvedCurrency)

  useEffect(() => {
    // Only act once we have a real project loaded (resolvedCurrency
    // defined). Pre-project state should still respect whatever the
    // operator picked globally — we only override inside the project.
    if (!resolvedCurrency) return
    if (isIdrContext(resolvedCurrency)) return
    if (i18n.language === 'en') return
    // Don't persist: when the operator backs out of the project (or
    // opens an IDR one next), App.tsx's mount-time localStorage check
    // restores their original preference.
    void i18n.changeLanguage('en')
  }, [resolvedCurrency, i18n])
}
